import { gaussianKDE } from "@/lib/science/plot-density";
import { boxStats } from "@/lib/science/plot-stats";
import { SCI_PALETTE } from "@/lib/science/sci-chart";
import { cartesianAxis, PlotShell } from "@/lib/science/sci-plot";
import { m } from "@/paraglide/messages";

const EXAMPLE =
  "Control,Drug A,Drug B\n5.1,7.2,4.0\n5.4,7.8,4.3\n4.9,6.9,3.8\n5.2,8.1,4.5\n5.6,7.5,4.1\n5.0,7.0,3.9\n5.3,8.4,4.7\n4.8,7.3,4.2\n5.5,7.9,4.4\n5.1,7.1,4.0";

export function ViolinPlot() {
  return (
    <PlotShell
      toolId="violin-plot"
      inputLabel={m.plot_input_label()}
      placeholder={m.violin_placeholder()}
      example={EXAMPLE}
      exampleLabel={m.plot_load_example()}
      hint={m.plot_hint_empty()}
      buildOption={(data, theme) => {
        const groups = data.colHeaders.map((name, ci) => {
          const values = data.matrix
            .map((row) => row[ci])
            .filter((n) => Number.isFinite(n));
          return {
            name,
            values,
            kde: gaussianKDE(values, 64),
            box: boxStats(values),
          };
        });
        const allVals = groups.flatMap((g) => g.values);
        const yMin = allVals.length ? Math.min(...allVals) : 0;
        const yMax = allVals.length ? Math.max(...allVals) : 1;
        const pad = (yMax - yMin) * 0.08 || 1;
        const maxDens = Math.max(1e-9, ...groups.flatMap((g) => g.kde.ys));
        const halfWidth = 0.42 / maxDens;

        return {
          tooltip: {
            trigger: "item",
            formatter: (p: { dataIndex: number }) => {
              const g = groups[p.dataIndex];
              if (!g?.box) return g?.name ?? "";
              return `${g.name}<br/>median ${g.box.median.toFixed(2)}<br/>Q1 ${g.box.q1.toFixed(2)} · Q3 ${g.box.q3.toFixed(2)}`;
            },
          },
          grid: { left: 52, right: 20, top: 20, bottom: 30 },
          xAxis: {
            type: "value",
            min: -0.6,
            max: groups.length - 0.4,
            interval: 1,
            axisLabel: {
              color: theme.muted,
              fontSize: 11,
              // biome-ignore lint/suspicious/noExplicitAny: ECharts formatter value.
              formatter: (v: any) => groups[Math.round(v)]?.name ?? "",
            },
            axisLine: { lineStyle: { color: theme.border } },
            splitLine: { show: false },
          },
          yAxis: {
            type: "value",
            min: yMin - pad,
            max: yMax + pad,
            ...cartesianAxis(theme),
          },
          series: [
            {
              type: "custom",
              // biome-ignore lint/suspicious/noExplicitAny: ECharts custom renderItem API.
              renderItem: (params: any, api: any) => {
                const gi = params.dataIndex;
                const g = groups[gi];
                if (!g || g.kde.xs.length === 0) return null;
                const color = SCI_PALETTE[gi % SCI_PALETTE.length];
                const pts: [number, number][] = [];
                for (let i = 0; i < g.kde.xs.length; i++) {
                  pts.push(
                    api.coord([gi - g.kde.ys[i] * halfWidth, g.kde.xs[i]]),
                  );
                }
                for (let i = g.kde.xs.length - 1; i >= 0; i--) {
                  pts.push(
                    api.coord([gi + g.kde.ys[i] * halfWidth, g.kde.xs[i]]),
                  );
                }
                const children: Array<{
                  type: string;
                  shape: Record<string, unknown>;
                  style: Record<string, unknown>;
                }> = [
                  {
                    type: "polygon",
                    shape: { points: pts },
                    style: {
                      fill: color,
                      opacity: 0.45,
                      stroke: color,
                      lineWidth: 1.5,
                    },
                  },
                ];
                if (g.box) {
                  const [x0, yMed] = api.coord([gi - 0.06, g.box.median]);
                  const [x1] = api.coord([gi + 0.06, g.box.median]);
                  children.push({
                    type: "line",
                    shape: { x1: x0, y1: yMed, x2: x1, y2: yMed },
                    style: { stroke: theme.text, lineWidth: 2 },
                  });
                }
                return { type: "group", children };
              },
              data: groups.map((_, gi) => [gi, 0]),
            },
          ],
        };
      }}
    />
  );
}
